import React, { useState, useEffect, useRef } from "react";
import { Redirect } from "react-router-dom";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useTunnelData } from "../TunnelContext";
import Api from "../baseApi";
import LoadingScreen from "../components/LoadingScreen";
import OAButton from "../components/OAButton";
import SubscriptionSummary from "../components/SubscriptionSummary";
import { ChevronLeft } from "lucide-react";
import Breadcrumbs from "../components/Breadcrumbs";
import { Link } from "react-router-dom/cjs/react-router-dom";
import { useTranslation } from "react-i18next";
import { CircleAlert } from "lucide-react";
import pushEvent from "../pushEvent";
import { useConfig } from "../ConfigProvider";

export default function Payment() {
  const config = useConfig();
  const { tunnel } = useTunnelData();
  if (config.code === "onair.ma" || tunnel.cmi) {
    return <CMIPayment />;
  }
  return <StripePayment />;
}

function CMIPayment() {
  const { currency } = useConfig();
  const { t } = useTranslation();
  const [step, stepSet] = useState("INIT");
  const { tunnel } = useTunnelData();
  const [form, formSet] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const doResaPayedByCard = () => {
    const [query] = Api.POST({
      path: "/api/abo/resamania2/payedByCard",
      body: {
        tunnelUid: tunnel.uid,
        msdsId: tunnel.club.msdsId,
        tunnel,
      },
    });
    return query;
  };

  useEffect(() => {
    if (step === "READY" || step === "RETRY") return;

    if (tunnel.cmi) {
      stepSet("VERIFY_PAYMENT");
      console.log("1) verify", tunnel.cmi);
      setTimeout(() => {
        if (tunnel.payment_status !== "PAYED") {
          stepSet("RETRY");
          return;
        }
        doResaPayedByCard()
          .then(() => {
            stepSet("PAYMENT_CONFIRMED");
          })
          .catch((e) => {
            //Ok we should not tell the user to retry the payment but there is a resamania2 bug
            console.error(e);
            stepSet("PAYMENT_CONFIRMED");
          });
      }, 2000);
    }
  }, []);

  if (!tunnel.sales) return <Redirect push to={"/"} />;

  const onSubmit = (e) => {
    e.preventDefault();
    stepSet("GO_TO_CMI");

    const [query] = Api.POST({
      path: "/api/abo/cmi/payment",
      body: tunnel,
    });

    query
      .then((ret) => {
        formSet(ret);
        setTimeout(() => inputRef.current.submit(), 1000);
      })
      .catch((err) => {
        console.error(err);
        stepSet("ERROR");
      });
    return;
  };

  const formDisplay = () => {
    return (
      <form action={form?.url || "null"} method="POST" ref={inputRef} style={{ display: "none" }}>
        {Object.entries(form?.body || {}).map(([k, v]) => {
          return <input readOnly={true} key={v} type="text" name={k} value={v} />;
        })}
      </form>
    );
  };

  if (step === "VERIFY_PAYMENT") {
    return <LoadingScreen LoadingText={t("loading_screen.payment_check")} />;
  }

  if (step === "PAYMENT_CONFIRMED") return <Redirect push to={"/confirmation"} />;

  return (
    <>
      {formDisplay()}
      {step === "GO_TO_CMI" && <LoadingScreen LoadingText={"Redirection vers CMI"} />}
      {step !== "GO_TO_CMI" && (
        <main className="w-full min-h-screen bg-oaBackgroundColor">
          <Link
            to="/contract"
            className="absolute left-4 top-5 md:left-20 flex flex-row hover:text-oaRed transition-all duration-300"
          >
            <ChevronLeft className="mr-2" />
            {t("previous_step")}
          </Link>
          <section className="w-full h-full flex flex-col items-center px-5 py-4 md:px-20">
            <div className="w-full animate-fade-in-left">
              <Breadcrumbs />
            </div>
            <div className="flex flex-row text-center justify-center text-[20px] md:text-[40px] gap-6 mb-1 mt-10">
              <h2 className="text-center font-bold animate-fade-in-left">{t("payment.title").toUpperCase()}</h2>
              <h2 className="text-center font-normal animate-fade-in-left ">
                {" "}
                TOTAL : {tunnel.sales.totalTI / 100}
                {currency}
              </h2>
            </div>
            <div>
              <p>
                Il vous reste la somme de {tunnel.sales.totalTI / 100}
                {currency} à régler,
              </p>
              <p>via CMI, le centre monétique interbancaire</p>
            </div>

            <div style={{ marginTop: "5rem" }}></div>

            {step === "RETRY" && (
              <div style={{ textAlign: "center" }}>
                <strong style={{ color: "red" }}>Votre paiement n'a pas été validé</strong>
                <p>Vous pouvez réessayer, vérifiez bien vos informations bancaires</p>
              </div>
            )}
            <div style={{ marginBottom: "5rem", marginTop: "1rem" }}>
              <OAButton
                onClick={onSubmit}
                className={"text-base font-extrabold animate-fade-in-right"}
                style={{ padding: "1.5rem" }}
              >
                Je paie avec CMI
              </OAButton>
            </div>

            <SubscriptionSummary
              club={tunnel.club}
              offer={tunnel.offer}
              options={tunnel.options}
              hidePromo={true}
              style={{ marginBottom: "5rem" }}
            />
          </section>

          <footer
            style={{
              position: "fixed",
              bottom: 0,
              width: "100%",
              textAlign: "center",
              padding: "1rem",
              backgroundColor: "#FFFFFF",
            }}
          >
            <div>Paiement sécurisé</div>
            <div style={{ display: "flex", justifyContent: "center", gap: "2rem" }}>
              <img alt="cmi" width="40" height="auto" src="/logo_cmi.png" />
              <img alt="secured" width="40" height="auto" src="/secure_code_logo.png" />
              <img alt="visa" width="40" height="auto" src="/tn_verified_by_visa.png" />
            </div>
          </footer>
        </main>
      )}
    </>
  );
}

function StripePayment() {
  const [step, stepSet] = useState("INIT");
  const { tunnel, setTunnel } = useTunnelData();
  const { currency } = useConfig();
  const [errors, setErrors] = useState({});
  const { t, i18n } = useTranslation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const doResaPayedByCard = () => {
    const [query] = Api.POST({
      path: "/api/abo/resamania2/payedByCard",
      body: {
        tunnelUid: tunnel.uid,
        msdsId: tunnel.club.msdsId,
        tunnel,
      },
    });
    return query;
  };

  useEffect(() => {
    if (step === "READY") return;

    if (tunnel.sales.totalTI === 0) {
      stepSet("SEPA");
    }

    if (tunnel.paymentIntent) {
      stepSet("VERIFY_PAYMENT");
      doResaPayedByCard()
        .then(() => {
          if (i18n.language === "fr") {
            stepSet("PAYMENT_CONFIRMED");
          } else {
            stepSet("PAYMENT_STEP_CONFIRMATION");
          }
        })
        .catch((e) => {
          if (e.statusCode === 409) {
            console.log("Already payed - everything's ok");
            return stepSet("PAYMENT_CONFIRMED");
          }
          stepSet("PAYMENT_ERROR");
        });
      return;
    }

    if (step === "INIT") {
      pushEvent({
        event: "funnel_step_5",
        step_name: "Abonnement",
        funnel_club: tunnel.club.name,
        funnel_club_status: "Ouvert",
        funnel_abo: tunnel.offer.offer.name,
      });
      const [query] = Api.POST({
        path: "/api/abo/stripe/paymentIntent",
        body: tunnel,
      });

      query
        .then((ret) => {
          setTunnel({
            ...tunnel,
            stripe: {
              ...ret,
              promise: loadStripe(ret.client_public),
            },
          });
          stepSet("READY");
        })
        .catch((err) => {
          console.error(err);
          stepSet("ERROR");
        });
      return;
    }

    console.error("unknow step", step);
  }, []);

  if (!tunnel.sales) return <Redirect push to={"/"} />;

  if (step === "SEPA") return <Redirect push to={"/sepa"} />;

  if (step === "INIT") return null;

  if (step === "VERIFY_PAYMENT") {
    return <LoadingScreen LoadingText={t("loading_screen.payment_check")} />;
  }
  if (step === "PAYMENT_STEP_CONFIRMATION") return <Redirect push to={"/middle-confirmation"} />;

  if (step === "PAYMENT_CONFIRMED") return <Redirect push to={"/confirmation"} />;

  if (step === "PAYMENT_ERROR") {
    return <LoadingScreen LoadingText={t("loading_screen.payment_error")} />;
  }

  if (step === "ERROR") {
    return <LoadingScreen LoadingText={t("loading_screen.stripe_not_configured")} />;
  }

  if (step === "STRIPE_PREPARE") {
    return <LoadingScreen LoadingText={t("loading_screen.loading_payment")} />;
  }

  const stripe_options = {
    clientSecret: tunnel.stripe.client_secret,
    locale: i18n.language,
  };

  return (
    <main className="w-full min-h-screen bg-oaBackgroundColor">
      <Link
        to="/contract"
        className="absolute left-4 top-5 md:left-20 flex flex-row hover:text-oaRed transition-all duration-300"
      >
        <ChevronLeft className="mr-2" />
        {t("previous_step")}
      </Link>
      <section className="w-full h-full flex flex-col items-center px-5 py-4 md:px-20">
        <div className="w-full animate-fade-in-left">
          <Breadcrumbs />
        </div>
        <div className="flex flex-row text-center justify-center text-[20px] md:text-[40px] gap-6 mb-1 mt-10">
          <h2 className="text-center font-bold animate-fade-in-left">{t("payment.title").toUpperCase()}</h2>
          <h2 className="text-center font-normal animate-fade-in-left ">
            {" "}
            TOTAL : {tunnel.sales.totalTI / 100}
            {currency}
          </h2>
        </div>

        {tunnel.stripe && (
          <Elements stripe={tunnel.stripe.promise} options={stripe_options}>
            <CardView />
          </Elements>
        )}

        <SubscriptionSummary club={tunnel.club} offer={tunnel.offer} options={tunnel.options} hidePromo={true} />
      </section>
    </main>
  );
}

const CardView = () => {
  const { tunnel } = useTunnelData();
  const [step, stepSet] = useState("INITIAL");
  const [cbError, setCbError] = useState(null);
  const stripe = useStripe();
  const elements = useElements();
  const { t } = useTranslation();

  const handleSubmit = async (event) => {
    event.preventDefault();
    stepSet("PENDING");
    setCbError(null);
    if (!stripe || !elements) return;

    const body = {
      elements,
      confirmParams: {
        return_url: window.location.origin + `/card?tunnelId=${tunnel.stripe.id}`,
      },
    };

    const result = await stripe.confirmPayment(body);
    if (result.error) {
      setCbError(result.error.message);
      stepSet("INITIAL");
      //WHY??
      //if (result.error.payment_intent.status === 'succeeded') return stepSet('SEPA');
    }
  };

  if (step)
    return (
      <div className="bg-white p-4 border border-[#C1C1C1] rounded-lg" style={{ borderRadius: "8px" }}>
        <form className="">
          <PaymentElement />
          {/* <div style={{display:'flex', alignItems:'center', margin:'auto, auto, 1rem, auto'}}> */}
          {/* <img src = 'payment-secure.png' alt='payment sécurisé' width='auto' height='25px'/> */}
          {/* </div> */}
        </form>

        <aside>
          {!!cbError && (
            <div className="text-center underline text-oaRed flex items-center justify-center gap-1">
              <CircleAlert className="size-5 " />
              <div>{cbError}</div>
            </div>
          )}

          <button
            className="w-full p-3 bg-[#8A8E9B1A] bg-oaRed rounded-[4px] font-extrabold text-white mt-6"
            type="button"
            onClick={handleSubmit}
            disabled={!stripe || step === "PENDING"}
          >
            {t("payment.stripe_button")}
          </button>
        </aside>
      </div>
    );
};
